import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import DescriptionIcon from '@mui/icons-material/Description';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import DocPreviewFrame from '../../../tools/docPreviewFrame';

// The print options of a label and the preview of what they print, shared by a pallet's record and by printing several pallets, so the two cannot
// offer different things. The preview IS the document: the same server answer the Print button sends to the printer.

// slab   one A5 label per pallet, in the language picked
// strip  one short label on a page of its own size: one pallet only, because a page has one size
// short  short labels on as many sheets as they need, for the paper in the printer
// The short labels are English, like the real sample, so they ask for no language.
export const LABEL_KINDS = [
  { value: 'slab',  labelKey: 'mis.labelKindSlab',  descKey: 'mis.labelKindSlabDesc',  usesLanguage: true },
  { value: 'strip', labelKey: 'mis.labelKindShort', descKey: 'mis.labelKindShortDesc', onePallet: true },
  { value: 'short', labelKey: 'mis.lblKindSheet',   descKey: 'mis.lblKindSheetDesc',   usesPaper: true },
];
export const kindsFor = (count) => LABEL_KINDS.filter((k) => !k.onePallet || count === 1);
export const kindInfo = (kind) => LABEL_KINDS.find((k) => k.value === kind) || LABEL_KINDS[0];

// Documents already fetched, for a minute: going back to a pallet, a language or a label just seen is then instant. The key holds every option
// and the record's refresh count, so an edit is never shown from here. Print sends the preview on screen, so what is printed is what was seen.
const CACHE_MS = 60000;
const CACHE_MAX = 40;
const cache = new Map();
const cached = (key) => {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > CACHE_MS) { cache.delete(key); return null; }
  return hit.html;
};
const remember = (key, html) => {
  cache.delete(key);
  cache.set(key, { html, at: Date.now() });
  while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
};

// How wide the paper is on screen: the preview is that wide, centred, and scales down on a phone instead of cutting the page off.
const pageWidth = (kind, paper) => {
  if (kind === 'strip') return 700;
  if (kind === 'short' && paper === 'a4') return 794;
  return 570;
};

// The document for these pallets and these options. `previewFor` is what the markup on screen belongs to: without it the previous pallet's label would
// stay up under the next pallet's header while the fetch runs, and a slower answer could overwrite a newer one.
export function useLabelPreview({ branchId, ids, kind, lang, paper, refreshKey = 0, enabled = true }) {
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const previewFor = useRef('');
  const idsKey = (ids || []).join(',');

  useEffect(() => {
    if (!enabled || !branchId || !idsKey) { previewFor.current = ''; setHtml(''); setLoading(false); return; }
    const qs = new URLSearchParams({ branchId, kind, lang });
    idsKey.split(',').forEach((id) => qs.append('id', id));
    if (kind === 'short') qs.append('paper', paper);
    const key = `${qs}|${refreshKey}`;
    previewFor.current = key;
    const hit = cached(key);
    if (hit) { setHtml(hit); setLoading(false); return; }
    setHtml(''); setLoading(true);
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/labels/html?${qs}`, responseType: 'text' })
      .then((res) => {
        const doc = typeof res.data === 'string' ? res.data : '';
        if (doc) remember(key, doc);
        if (previewFor.current === key) setHtml(doc);
      })
      .catch(() => { if (previewFor.current === key) setHtml(''); })
      .finally(() => { if (previewFor.current === key) setLoading(false); });
  }, [enabled, branchId, idsKey, kind, lang, paper, refreshKey, authCtx, axiosGlobal]);

  return { html, loading };
}

// The label's paper on a darker desk, so a label reads as a page and not as a part of the screen.
export function LabelPreview({ html, loading, kind, paper, title, height = { xs: 460, md: 820 }, T }) {
  const { t } = useTranslation();
  const width = pageWidth(kind, paper);
  return (
    <Box data-testid="lbl-preview" sx={{ display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 2 }, borderRadius: '12px', bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}` }}>
      <Box sx={{ width: '100%', maxWidth: width, bgcolor: '#fff', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.22)' }}>
        {loading && !html ? (
          <Skeleton variant="rectangular" height={520} />
        ) : html ? (
          <DocPreviewFrame html={html} title={title} height={height} docWidth={width} />
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 28, color: 'rgba(0,0,0,0.3)', mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)' }}>{t('mis.previewUnavailable')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function LabelKindSelect({ value, onChange, count, touch, sx }) {
  const { t } = useTranslation();
  return (
    <Select size="small" value={value} onChange={(e) => onChange(e.target.value)} inputProps={{ 'aria-label': t('mis.lblPrintKind') }}
      SelectDisplayProps={{ 'data-testid': 'lbl-kind' }} renderValue={(v) => t(kindInfo(v).labelKey)}
      sx={{ height: touch ? 40 : 28, fontSize: touch ? '0.8rem' : '0.72rem', minWidth: 130, ...sx }}>
      {kindsFor(count).map((k) => (
        <MenuItem key={k.value} value={k.value} data-testid={`lbl-kind-${k.value}`} sx={{ display: 'block', py: 0.75, whiteSpace: 'normal', maxWidth: 320 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{t(k.labelKey)}</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{t(k.descKey)}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
}

export function LabelLanguageSelect({ value, onChange, touch, sx }) {
  const { t } = useTranslation();
  return (
    <Select size="small" value={value} onChange={(e) => onChange(e.target.value)} inputProps={{ 'aria-label': t('mis.lblLanguage') }}
      SelectDisplayProps={{ 'data-testid': 'lbl-language' }}
      sx={{ height: touch ? 40 : 28, fontSize: touch ? '0.8rem' : '0.72rem', minWidth: 86, ...sx }}>
      <MenuItem value="en" sx={{ fontSize: '0.78rem' }}>English</MenuItem>
      <MenuItem value="ar" sx={{ fontSize: '0.78rem' }}>العربية</MenuItem>
      <MenuItem value="fa" sx={{ fontSize: '0.78rem' }}>فارسی</MenuItem>
    </Select>
  );
}

export function LabelPaperToggle({ value, onChange, disabled, touch }) {
  const { t } = useTranslation();
  return (
    <ToggleButtonGroup size="small" exclusive value={value} disabled={disabled} aria-label={t('mis.plShortSheetPaper')}
      onChange={(_, v) => { if (v) onChange(v); }}>
      {['a5', 'a4'].map((paper) => (
        <ToggleButton key={paper} value={paper} data-testid={`lbl-print-paper-${paper}`}
          sx={{ px: 1.25, py: 0.15, fontSize: '0.72rem', fontWeight: 700, ...(touch ? { minHeight: 40, minWidth: 48 } : { height: 28 }) }}>
          {paper.toUpperCase()}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
